/**
 * Small DOM helpers shared by the placeholder surface renderers.
 *
 * These build real `@caos/ui` custom elements and token-driven host chrome. The surfaces in
 * this folder are intentionally MINIMAL placeholders that prove the host contract end to end;
 * the pixel-faithful builds land in their own tickets (sign-in CAOS-132, empty-workspace
 * CAOS-133, hero/line-art CAOS-131), plugging into the exact same `SurfaceRenderer` seam.
 */

/** A `<caos-button>` with a variant, optional slot, and text label. */
export function button(
  label: string,
  variant: 'primary' | 'secondary' | 'soft' | 'ghost' | 'destructive',
  slot?: string,
): HTMLElement {
  const b = document.createElement('caos-button');
  b.setAttribute('variant', variant);
  if (slot) b.setAttribute('slot', slot);
  b.textContent = label;
  return b;
}

/** A host-chrome card container. */
export function card(): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'host-card';
  return el;
}

/** The brand line + a sub-heading for a card. */
export function brandline(subtitle: string): DocumentFragment {
  const frag = document.createDocumentFragment();

  const line = document.createElement('div');
  line.className = 'host-brandline';
  const mark = document.createElement('span');
  mark.className = 'host-mark';
  mark.setAttribute('aria-hidden', 'true');
  mark.textContent = '◆'; // ◆ — the default brand mark; a real wordmark lands in CAOS-131/132.
  const word = document.createElement('span');
  word.className = 'host-wordmark';
  word.textContent = 'Cloud Atlantis OS';
  line.append(mark, word);

  const sub = document.createElement('p');
  sub.className = 'host-sub';
  sub.textContent = subtitle;

  frag.append(line, sub);
  return frag;
}

/** A stacked, labelled placeholder input row (native input — CAOS-132 swaps in caos-text-field). */
export function field(label: string, type: string): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.className = 'host-field';

  const lab = document.createElement('label');
  lab.className = 'host-field-label';
  lab.textContent = label;

  const input = document.createElement('input');
  input.className = 'host-input';
  input.type = type;
  input.setAttribute('autocomplete', type === 'password' ? 'current-password' : 'username');

  wrap.append(lab, input);
  return wrap;
}

/** A vertical stack of action controls. */
export function actions(...controls: readonly Node[]): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'host-actions';
  el.append(...controls);
  return el;
}
